/**
 * MainWorkspace - Primary content display area
 *
 * Displays content from AUI buffers with beautiful markdown rendering.
 * Features:
 * - GitHub Flavored Markdown (tables, task lists, etc.)
 * - KaTeX math rendering (lazy loaded)
 * - Reddit-style extensions (spoilers, superscript)
 * - Syntax highlighted code blocks
 * - Split-pane mode for before/after comparison
 * - Thread mode: message-level navigation with outline sidebar
 * - Side-by-side edit mode with live preview
 *
 * Shows WelcomeScreen when no content is active.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { WelcomeScreen } from './WelcomeScreen';
import { MessageNavigator } from './MessageNavigator';
import { RoleFilter } from './RoleFilter';
import { useBufferSync, type ContentItem } from '../../contexts/BufferSyncContext';
import { useApi } from '../../contexts/ApiContext';
import { useArchiveApi } from '../../hooks/useArchiveApi';
import { useIdleDetection } from '../../hooks/useIdleDetection';
import { useProject } from '../../contexts/ProjectContext';
import { useBook } from '../../contexts/BookContext';
import { useChapterStackSync } from '../../hooks/useChapterStackSync';
import { BufferPicker } from './BufferPicker';
import { BufferHistory } from './BufferHistory';
import { BufferManagerDrawer } from './BufferManagerDrawer';
import { CardStack } from './CardStack';
import { VersionBar, type ContentVersion } from './VersionBar';

import { ProjectPicker } from './ProjectPicker';
import { WorkspaceCurator } from './WorkspaceCurator';
import { OutlinePanelInline, type OutlineBookContext } from './OutlinePanel';
import { WorkspaceEditView } from './WorkspaceEditView';
import { WorkspaceSplitView } from './WorkspaceSplitView';
import { WorkspaceReadView } from './WorkspaceReadView';
import { useWorkspaceKeyboard } from '../../hooks/useWorkspaceKeyboard';
import { useDraftPersistence } from '../../hooks/useDraftPersistence';
import { useThreadNavigation } from '../../hooks/useThreadNavigation';
import { useOverrideManagement } from '../../hooks/useOverrideManagement';
import { useEditMode } from '../../hooks/useEditMode';
import { DraftEditorInline } from './DraftEditorInline';
import { BookPlanView } from './BookPlanView';
import type { FindBuffer } from '../archive/CuratorView';
import { useToast } from '../../contexts/ToastContext';
import { createLogger } from '../../utils/debug';

// Re-export types for backward compatibility
export type { WorkspaceContent, SplitContent, MainWorkspaceProps, ViewMode, WorkspaceMode } from './workspace-types';
import type { ViewMode, WorkspaceMode, MainWorkspaceProps } from './workspace-types';

const log = createLogger('Workspace');

/** Overflow menu (⋮) for secondary actions */
function OverflowMenu({ onFindSimilar, onFindRelated, onAddToVoice, onExportMarkdown, onExportPdf }: {
  onFindSimilar?: () => void;
  onFindRelated?: () => void;
  onAddToVoice: () => void;
  onExportMarkdown?: () => void;
  onExportPdf?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  return (
    <div className="workspace__overflow" ref={ref}>
      <button
        className="workspace__icon-btn"
        onClick={() => setOpen(v => !v)}
        title="More actions"
        aria-label="More actions"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="3" r="1.5"/>
          <circle cx="8" cy="8" r="1.5"/>
          <circle cx="8" cy="13" r="1.5"/>
        </svg>
      </button>
      {open && (
        <div className="workspace__overflow-dropdown">
          {onFindSimilar && (
            <button className="workspace__overflow-item" onClick={() => { onFindSimilar(); setOpen(false); }}>
              Find Similar
            </button>
          )}
          {onFindRelated && (
            <button className="workspace__overflow-item" onClick={() => { onFindRelated(); setOpen(false); }}>
              Find Related in Archive
            </button>
          )}
          <button className="workspace__overflow-item" onClick={() => { onAddToVoice(); setOpen(false); }}>
            Add to Voice
          </button>
          {onExportMarkdown && (
            <button className="workspace__overflow-item" onClick={() => { onExportMarkdown(); setOpen(false); }}>
              Export as Markdown
            </button>
          )}
          {onExportPdf && (
            <button className="workspace__overflow-item" onClick={() => { onExportPdf(); setOpen(false); }}>
              Export as PDF
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function MainWorkspace({ content, messages, onFindSimilar, splitContent, onSplitDismissed, highlightText, sentencingData, roleClassifierData, initialMessageIndex, onSearchInThread, onMediaClick, onConversationClick, threadIndex, onPrevThread, onNextThread, onClearPendingNavigation, curatorFindBuffer, onCuratorFindBufferConsumed, requestedViewMode, onRequestedViewModeConsumed, requestedWorkspaceMode, onRequestedWorkspaceModeConsumed, onViewModeChange, noemaGraphData, sicData, chapterGraphData }: MainWorkspaceProps) {
  const { setContent, commit, isDirty, undo, redo, canUndo, canRedo, selectedText, setSelectedText, setFocusedText, canGoBack, canGoForward, contentGoBack, contentGoForward, contentHistoryLabel, transformChain, activeChainBufferId, switchToChainVersion, syncError, stackItems, activeStackIndex, addToStack, importText } = useBufferSync();
  const api = useApi();
  const archiveApi = useArchiveApi();
  const isLocalArchive = archiveApi.isLocal;
  const { activeProject, addBufferToProject, updateProject } = useProject();
  const { books, activeBook, chapters, activeChapter, activeChapterId, setActiveBook, setActiveChapter, createBook, createChapter, updateChapter, addCardToChapter, saveChapterOutline, saveChapterDraftBuffer } = useBook();
  const { restoredOutline, restoredDraft, consumeRestoredOutline, consumeRestoredDraft } = useChapterStackSync();
  const { addToast } = useToast();

  // Build book context for chapter-aware outline/draft generation (async for cross-chapter data)
  const [bookContext, setBookContext] = useState<OutlineBookContext | undefined>(undefined);
  useEffect(() => {
    if (!activeBook || !activeChapter) {
      setBookContext(undefined);
      return;
    }
    const chapterMeta = (activeChapter.metadata ?? {}) as Record<string, unknown>;

    // Start with synchronous data
    const base: OutlineBookContext = {
      bookTitle: activeBook.title,
      bookDescription: activeBook.description || undefined,
      chapterTitle: activeChapter.title,
      chapterTheme: (chapterMeta.theme as string) || undefined,
      chapterSummary: (chapterMeta.summary as string) || undefined,
      chapterPosition: activeChapter.position,
      totalChapters: chapters.length,
      siblingChapterTitles: chapters
        .filter(c => c.id !== activeChapter.id)
        .sort((a, b) => a.position - b.position)
        .map(c => c.title),
      chapterObligation: chapterMeta.strongestObjection ? {
        strongestObjection: chapterMeta.strongestObjection as string,
        howChapterAnswers: (chapterMeta.howChapterAnswers as string) ?? '',
        whatWouldBeLostIfRemoved: (chapterMeta.whatWouldBeLostIfRemoved as string) ?? '',
      } : undefined,
    };

    // Set synchronous context immediately so it's never stale
    setBookContext(base);

    // Async: load prior chapter data for cross-chapter coherence
    const priorChapters = chapters
      .filter(c => c.id !== activeChapter.id && c.position < activeChapter.position)
      .sort((a, b) => a.position - b.position);

    if (priorChapters.length === 0 || !window.electronBuffer?.load) return;

    let cancelled = false;
    (async () => {
      const summaries: Array<{ title: string; summary: string }> = [];
      const concepts: string[] = [];
      const phrases: string[] = [];
      let priorChapterHooks: string[] = [];

      for (const ch of priorChapters) {
        if (cancelled) return;
        const meta = (ch.metadata ?? {}) as Record<string, unknown>;

        // Gather extractedInsights from prior chapter metadata
        const insights = meta.extractedInsights as { concepts?: string[]; phrases?: string[]; hooksPlanted?: string[] } | undefined;
        if (insights?.concepts) concepts.push(...insights.concepts);
        if (insights?.phrases) phrases.push(...insights.phrases);

        // Gather hooks from the immediately preceding chapter only
        if (ch.position === activeChapter!.position - 1 && insights?.hooksPlanted?.length) {
          priorChapterHooks = insights.hooksPlanted;
        }

        // Load prior draft buffer to extract summary (~first 300 words)
        if (ch.draftBufferId) {
          try {
            const buf = await window.electronBuffer!.load(ch.draftBufferId);
            if (buf?.text) {
              const words = buf.text.split(/\s+/).filter(Boolean);
              const excerpt = words.slice(0, 300).join(' ');
              summaries.push({ title: ch.title, summary: excerpt });
            }
          } catch {
            // Non-fatal — prior draft may have been deleted
          }
        } else if (meta.summary) {
          // Fallback: use chapter's planning summary
          summaries.push({ title: ch.title, summary: meta.summary as string });
        }
      }

      if (cancelled) return;

      setBookContext(prev => prev ? {
        ...prev,
        priorChapterSummaries: summaries.length > 0 ? summaries : undefined,
        priorConceptRegistry: concepts.length > 0 ? concepts : undefined,
        priorPhraseCorpus: phrases.length > 0 ? phrases : undefined,
        priorChapterHooks: priorChapterHooks.length > 0 ? priorChapterHooks : undefined,
      } : prev);
    })();

    return () => { cancelled = true; };
  }, [activeBook, activeChapter, chapters]);

  // Dual workspace mode: Archive (read/edit/split) vs Project (stack/plan/outline/draft)
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>('archive');
  const [archiveViewMode, setArchiveViewMode] = useState<ViewMode>('read');
  const [projectViewMode, setProjectViewMode] = useState<ViewMode>('stack');

  // Derived effective viewMode — all existing code reads this unchanged
  const viewMode = workspaceMode === 'archive' ? archiveViewMode : projectViewMode;
  const setViewMode = useCallback((mode: ViewMode | ((prev: ViewMode) => ViewMode)) => {
    if (workspaceMode === 'archive') setArchiveViewMode(mode);
    else setProjectViewMode(mode);
  }, [workspaceMode]);

  // Notify parent when viewMode changes (for sibling components)
  useEffect(() => {
    onViewModeChange?.(viewMode);
  }, [viewMode, onViewModeChange]);

  const [stackEditMode, setStackEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');

  // B10/C6: View with Sources split state — now includes provenance
  const [sourceReferenceView, setSourceReferenceView] = useState<{
    draftText: string;
    sourceCards: Array<{ title: string; excerpt: string }>;
    provenances?: Array<{
      type: 'verbatim' | 'paraphrase' | 'synthesis';
      sourceCardIndices: number[];
      startOffset: number;
      endOffset: number;
    }>;
  } | null>(null);

  // C6/C7: Active source card index for bidirectional navigation
  const [activeSourceIndex, setActiveSourceIndex] = useState<number | null>(null);

  // Buffer Manager drawer state
  const [showBufferManager, setShowBufferManager] = useState(false);

  // D1: Workspace Curator modal state
  const [showCurator, setShowCurator] = useState(false);
  const [curatorInitialQuery, setCuratorInitialQuery] = useState<string | null>(null);
  const [curatorInitialFindBuffer, setCuratorInitialFindBuffer] = useState<FindBuffer | undefined>(undefined);

  // Toolbar paste dropdown
  const [showToolbarPaste, setShowToolbarPaste] = useState(false);
  const [toolbarPasteText, setToolbarPasteText] = useState('');
  const [toolbarPasteImporting, setToolbarPasteImporting] = useState(false);
  const toolbarPasteRef = useRef<HTMLDivElement>(null);

  // M5: Open curator modal when a saved search is requested from ProjectBrowser
  useEffect(() => {
    if (curatorFindBuffer) {
      setCuratorInitialFindBuffer(curatorFindBuffer);
      setCuratorInitialQuery(null);
      setShowCurator(true);
      onCuratorFindBufferConsumed?.();
    }
  }, [curatorFindBuffer, onCuratorFindBufferConsumed]);

  // Consume external view mode request — also auto-switch workspace mode
  useEffect(() => {
    if (requestedViewMode) {
      const projectModes: ViewMode[] = ['stack', 'book-plan', 'outline', 'draft'];
      if (projectModes.includes(requestedViewMode)) {
        setWorkspaceMode('project');
        setProjectViewMode(requestedViewMode);
      } else {
        setWorkspaceMode('archive');
        setArchiveViewMode(requestedViewMode as ViewMode);
      }
      onRequestedViewModeConsumed?.();
    }
  }, [requestedViewMode, onRequestedViewModeConsumed]);

  // Consume external workspace mode request
  useEffect(() => {
    if (requestedWorkspaceMode) {
      setWorkspaceMode(requestedWorkspaceMode);
      onRequestedWorkspaceModeConsumed?.();
    }
  }, [requestedWorkspaceMode, onRequestedWorkspaceModeConsumed]);

  // ─── Extracted hooks ───────────────────────────────────────────
  const draft = useDraftPersistence({
    activeChapterId,
    activeChapter,
    activeProject,
    books,
    setActiveBook,
    createBook,
    createChapter,
    saveChapterOutline,
    saveChapterDraftBuffer,
    updateChapter,
    addBufferToProject,
    addToast,
    setViewMode,
    restoredOutline,
    consumeRestoredOutline,
    restoredDraft,
    consumeRestoredDraft,
  });

  const thread = useThreadNavigation({
    messages,
    content,
    draftText: draft.draftText,
    viewMode,
    stackItems,
    activeStackIndex,
    setFocusedText,
    initialMessageIndex,
    onClearPendingNavigation,
  });

  const override = useOverrideManagement({
    isThreadMode: thread.isThreadMode,
    currentMessage: thread.currentMessage,
    displayText: thread.displayText,
    content,
    editContent,
    api,
    isLocalArchive,
    addToast,
    setViewMode,
  });

  const edit = useEditMode({
    content,
    editContent,
    setEditContent,
    viewMode,
    setViewMode,
    isThreadMode: thread.isThreadMode,
    messages,
    activeMessageIndex: thread.activeMessageIndex,
    currentNodeId: override.currentNodeId,
    setHasOverride: override.setHasOverride,
    setContent,
    commit,
    api,
    isLocalArchive,
    splitContent: splitContent ?? null,
    onSplitDismissed,
    addToast,
  });

  // Distraction-free: chrome fades after 3s idle in read mode
  const isIdle = useIdleDetection(3000);

  // Enter split mode when splitContent is provided
  useEffect(() => {
    if (splitContent) {
      setViewMode('split');
    }
  }, [splitContent]);

  // Reset stack edit mode when leaving stack view
  useEffect(() => {
    if (viewMode !== 'stack') {
      setStackEditMode(false);
    }
  }, [viewMode]);

  // Keyboard shortcuts (extracted to hook)
  useWorkspaceKeyboard({
    viewMode,
    setViewMode,
    workspaceMode,
    canUndo,
    canRedo,
    undo,
    redo,
    hasContent: !!content,
    handleEnterEdit: edit.handleEnterEdit,
    hasSplitContent: !!splitContent,
    hasDraftText: !!draft.draftText,
    stackEditMode,
    setStackEditMode,
    isThreadMode: thread.isThreadMode,
    goPrev: thread.goPrev,
    goNext: thread.goNext,
    goFirst: thread.goFirst,
    goLast: thread.goLast,
    contentGoBack,
    contentGoForward,
  });

  const handleSelection = useCallback(() => {
    const selection = window.getSelection();
    const text = selection?.toString().trim() || null;
    setSelectedText(text && text.length > 0 ? text : null);
  }, [setSelectedText]);

  const threadId = thread.isThreadMode ? messages?.[0]?.metadata.source?.threadId : undefined;

  const handleAddMessageToStack = useCallback(async () => {
    if (!thread.isThreadMode || !thread.currentMessage) return;
    const msg = thread.currentMessage;

    // Check for duplicate before adding — show toast if already in stack
    const nodeId = msg.metadata.source?.nodeId;
    if (nodeId && stackItems.some(s => s.sourceNodeId === nodeId)) {
      addToast('Already in stack', 'info');
      return;
    }

    const item: import('../../contexts/BufferSyncContext').ContentItem = {
      id: crypto.randomUUID(),
      type: 'archive-node',
      text: msg.text,
      metadata: {
        source: {
          type: msg.metadata.source?.type ?? 'archive',
          path: msg.metadata.source?.path ?? ['Message'],
          nodeId: msg.metadata.source?.nodeId,
          threadId: msg.metadata.source?.threadId,
          threadTitle: msg.metadata.source?.threadTitle,
        },
        authorRole: msg.metadata.authorRole,
        timestamp: msg.metadata.timestamp,
        wordCount: msg.metadata.wordCount ?? msg.text.split(/\s+/).filter(Boolean).length,
      },
    };
    try {
      await addToStack(item);
      addToast('Added to stack', 'success');
    } catch (err) {
      log.warn('Failed to add message to stack:', err);
      addToast('Failed to add to stack', 'warning');
    }
  }, [thread.isThreadMode, thread.currentMessage, addToStack, stackItems, addToast]);

  // B10/C6: View with Sources — now accepts provenances
  // Track which view triggered source view so we can return there on dismiss
  const sourceReturnViewRef = useRef<ViewMode>('stack');
  const handleViewWithSources = useCallback((draftText: string, sourceCards: Array<{ title: string; excerpt: string }>, provenances?: Array<{
    type: 'verbatim' | 'paraphrase' | 'synthesis';
    sourceCardIndices: number[];
    startOffset: number;
    endOffset: number;
  }>) => {
    sourceReturnViewRef.current = viewMode;
    setSourceReferenceView({ draftText, sourceCards, provenances });
    setActiveSourceIndex(null);
    setViewMode('split');
  }, [viewMode]);

  const handleDismissSourceView = useCallback(() => {
    setSourceReferenceView(null);
    setViewMode(sourceReturnViewRef.current);
  }, [setViewMode]);

  // 5a/5b: Auto-create book+chapter when clicking Outline with no active chapter
  const handleOutlineClick = useCallback(async () => {
    if (!activeChapter && activeProject && window.electronBook && stackItems.length >= 2) {
      try {
        const chapterId = await draft.ensureBookAndChapter();
        if (chapterId) {
          for (let i = 0; i < stackItems.length; i++) {
            await addCardToChapter(stackItems[i].bufferId, i, chapterId);
          }
          addToast('Book created from stack', 'success');
        }
      } catch (err) {
        log.warn('Auto-create book failed:', err);
      }
    }
    setViewMode('outline');
  }, [activeChapter, activeProject, stackItems, draft.ensureBookAndChapter, addCardToChapter, addToast]);

  // Called when CardStack opens its outline modal — ensure chapter exists before generation
  const handleCardStackOutlineOpen = useCallback(async () => {
    log.info('[handleCardStackOutlineOpen] activeChapter:', !!activeChapter, 'activeProject:', !!activeProject, 'stackItems:', stackItems.length);
    if (!activeChapter && activeProject && stackItems.length >= 2) {
      try {
        const chapterId = await draft.ensureBookAndChapter();
        log.info('[handleCardStackOutlineOpen] ensureBookAndChapter returned:', chapterId);
        if (chapterId) {
          for (let i = 0; i < stackItems.length; i++) {
            await addCardToChapter(stackItems[i].bufferId, i, chapterId);
          }
          log.info('[handleCardStackOutlineOpen] Added', stackItems.length, 'cards to chapter');
        }
      } catch (err) {
        log.error('[handleCardStackOutlineOpen] Auto-create book failed:', err);
      }
    }
  }, [activeChapter, activeProject, stackItems, draft.ensureBookAndChapter, addCardToChapter]);

  const handleToolbarPaste = useCallback(async () => {
    const text = toolbarPasteText.trim();
    if (!text) return;
    setToolbarPasteImporting(true);
    try {
      const title = text.slice(0, 50).replace(/\n/g, ' ');
      await importText(text, title);
      setToolbarPasteText('');
      setShowToolbarPaste(false);
    } catch (err) {
      log.error('Failed to import pasted text:', err);
    } finally {
      setToolbarPasteImporting(false);
    }
  }, [toolbarPasteText, importText]);

  // Close toolbar paste dropdown on outside click
  useEffect(() => {
    if (!showToolbarPaste) return;
    const handleClick = (e: MouseEvent) => {
      if (toolbarPasteRef.current && !toolbarPasteRef.current.contains(e.target as Node)) {
        setShowToolbarPaste(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showToolbarPaste]);

  const hasSplit = content && ((splitContent !== null && splitContent !== undefined) || sourceReferenceView !== null);

  // Hide nav/buffer controls in modes where they could destroy state
  const showNavControls = viewMode !== 'outline' && viewMode !== 'draft' && viewMode !== 'book-plan';

  // Thread title for breadcrumb
  const threadTitle = content && thread.isThreadMode && messages
    ? messages[0]?.metadata.source?.threadTitle ?? content.title
    : null;

  return (
    <div className={`workspace${viewMode === 'read' ? ' workspace--distraction-free' : ''}${viewMode === 'read' && isIdle ? ' workspace--idle' : ''}`} onMouseUp={handleSelection}>
      {/* Breadcrumb */}
      {content && thread.isThreadMode ? (
        <nav className="workspace__breadcrumb">
          <span>{threadTitle}</span>
          {thread.currentMessage?.metadata.timestamp && (
            <span className="workspace__breadcrumb-date">
              {new Date(thread.currentMessage.metadata.timestamp).toLocaleDateString()}
            </span>
          )}
          {thread.displayRole && (
            <span className="workspace__breadcrumb-role"> ({thread.displayRole})</span>
          )}
          {override.hasOverride && (
            <span className="workspace__override-badge">EDITED</span>
          )}
        </nav>
      ) : content?.source?.path ? (
        <nav className="workspace__breadcrumb">
          {content.source.path.map((p, i) => (
            <span key={i}>
              {i > 0 && <span className="workspace__breadcrumb-sep">&rsaquo;</span>}
              {p}
            </span>
          ))}
          {content.metadata?.timestamp && (
            <span className="workspace__breadcrumb-date">
              {new Date(content.metadata.timestamp).toLocaleDateString()}
            </span>
          )}
          {override.hasOverride && (
            <span className="workspace__override-badge">EDITED</span>
          )}
        </nav>
      ) : null}

      {/* Chapter breadcrumb */}
      {activeBook && activeChapter && (
        <div className="workspace__chapter-breadcrumb">
          <span className="workspace__chapter-breadcrumb-book">{activeBook.title}</span>
          <span className="workspace__chapter-breadcrumb-sep">&rsaquo;</span>
          <span className="workspace__chapter-breadcrumb-chapter">{activeChapter.title}</span>
          {window.electronBook?.exportPdf && (
            <button
              className="workspace__chapter-breadcrumb-export"
              onClick={async () => {
                try {
                  const result = await window.electronBook!.exportPdf(activeBook.id);
                  if (result.success) {
                    addToast('PDF exported', 'success');
                  } else if (!result.cancelled) {
                    addToast('PDF export failed', 'warning');
                  }
                } catch (err) {
                  log.error('PDF export failed:', err);
                  addToast('PDF export failed', 'warning');
                }
              }}
              title="Export book as PDF"
              aria-label="Export PDF"
            >
              PDF
            </button>
          )}
          <button
            className="workspace__chapter-breadcrumb-close"
            onClick={() => setActiveChapter(null)}
            title="Deactivate chapter"
            aria-label="Close chapter"
          >
            &times;
          </button>
        </div>
      )}

      {/* Workspace Toolbar — two fixed rows */}
      <div className="workspace__toolbar">
        {/* Row 1: Navigation */}
        <div className="workspace__toolbar-nav">
          <div className="workspace__toolbar-left">
            {showNavControls && (
              <>
                <button
                  className="workspace__nav-btn"
                  onClick={contentGoBack}
                  disabled={!canGoBack}
                  title="Back (Alt+Left)"
                  aria-label="Go back"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M10.354 3.354a.5.5 0 0 0-.708-.708l-5 5a.5.5 0 0 0 0 .708l5 5a.5.5 0 0 0 .708-.708L5.707 8l4.647-4.646z"/>
                  </svg>
                </button>
                <button
                  className="workspace__nav-btn"
                  onClick={contentGoForward}
                  disabled={!canGoForward}
                  title="Forward (Alt+Right)"
                  aria-label="Go forward"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M5.646 3.354a.5.5 0 0 1 .708-.708l5 5a.5.5 0 0 1 0 .708l-5 5a.5.5 0 0 1-.708-.708L10.293 8 5.646 3.354z"/>
                  </svg>
                </button>
                {contentHistoryLabel && (
                  <span className="workspace__nav-label" title={contentHistoryLabel}>
                    {contentHistoryLabel}
                  </span>
                )}
                {threadIndex && (
                  <div className="workspace__thread-nav">
                    <button
                      className="workspace__nav-btn"
                      onClick={onPrevThread}
                      disabled={threadIndex.currentIndex <= 0}
                      title="Previous conversation"
                      aria-label="Previous conversation"
                    >
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M10.354 3.354a.5.5 0 0 0-.708-.708l-5 5a.5.5 0 0 0 0 .708l5 5a.5.5 0 0 0 .708-.708L5.707 8l4.647-4.646z"/></svg>
                    </button>
                    <span className="workspace__thread-nav-label">
                      {threadIndex.currentIndex + 1}/{threadIndex.threadIds.length}
                    </span>
                    <button
                      className="workspace__nav-btn"
                      onClick={onNextThread}
                      disabled={threadIndex.currentIndex >= threadIndex.threadIds.length - 1}
                      title="Next conversation"
                      aria-label="Next conversation"
                    >
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M5.646 3.354a.5.5 0 0 1 .708-.708l5 5a.5.5 0 0 1 0 .708l-5 5a.5.5 0 0 1-.708-.708L10.293 8 5.646 3.354z"/></svg>
                    </button>
                  </div>
                )}
                <BufferPicker />
              </>
            )}
            <ProjectPicker />
            <div className="workspace__paste-dropdown-wrapper" ref={toolbarPasteRef}>
              <button
                className={`workspace__icon-btn${showToolbarPaste ? ' workspace__icon-btn--active' : ''}`}
                onClick={() => setShowToolbarPaste(prev => !prev)}
                title="Paste text (new buffer)"
                aria-label="Paste text"
                aria-expanded={showToolbarPaste}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M3.626 3.533a.249.249 0 0 0-.126.217v9.5c0 .138.112.25.25.25h8.5a.25.25 0 0 0 .25-.25v-9.5a.249.249 0 0 0-.126-.217.248.248 0 0 0-.25.008A2.22 2.22 0 0 1 10.874 4H5.126a2.22 2.22 0 0 1-1.25-.459.248.248 0 0 0-.25-.008ZM5.126 2.5a.75.75 0 0 0 .593-1.213A.878.878 0 0 1 6.25.5h3.5a.877.877 0 0 1 .531.787.75.75 0 0 0 .593 1.213h.001a3.721 3.721 0 0 0 1.902-.533A1.749 1.749 0 0 1 14 3.75v9.5A1.75 1.75 0 0 1 12.25 15h-8.5A1.75 1.75 0 0 1 2 13.25v-9.5c0-.631.335-1.213.879-1.532A3.721 3.721 0 0 0 4.781 2.5Z"/>
                </svg>
              </button>
              {showToolbarPaste && (
                <div className="workspace__paste-dropdown">
                  <textarea
                    className="workspace__paste-dropdown-textarea"
                    placeholder="Paste or type text..."
                    value={toolbarPasteText}
                    onChange={e => setToolbarPasteText(e.target.value)}
                    rows={4}
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        handleToolbarPaste();
                      }
                      if (e.key === 'Escape') {
                        setShowToolbarPaste(false);
                      }
                    }}
                  />
                  <div className="workspace__paste-dropdown-actions">
                    <button
                      className="workspace__paste-dropdown-btn workspace__paste-dropdown-btn--import"
                      onClick={handleToolbarPaste}
                      disabled={!toolbarPasteText.trim() || toolbarPasteImporting}
                    >
                      {toolbarPasteImporting ? 'Importing...' : 'Import'}
                    </button>
                    <span className="workspace__paste-dropdown-hint">Cmd+Enter</span>
                  </div>
                </div>
              )}
            </div>
            {viewMode === 'stack' && activeProject && (
              <input
                className="workspace__project-name-input"
                type="text"
                defaultValue={activeProject.name}
                key={activeProject.id}
                onBlur={(e) => {
                  const newName = e.target.value.trim();
                  if (newName && newName !== activeProject.name) {
                    updateProject(activeProject.id, { name: newName }).catch(() => addToast('Failed to rename project', 'warning'));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  if (e.key === 'Escape') {
                    (e.target as HTMLInputElement).value = activeProject.name;
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                title="Click to rename project"
                aria-label="Project name"
              />
            )}
          </div>
          <div className="workspace__toolbar-right">
            {/* Buffer Manager drawer toggle */}
            <button
              className={`workspace__icon-btn${showBufferManager ? ' workspace__icon-btn--active' : ''}`}
              onClick={() => setShowBufferManager(v => !v)}
              title="Manage Buffers"
              aria-label="Manage Buffers"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="1" y="1" width="14" height="3" rx="0.5"/>
                <rect x="1" y="6.5" width="14" height="3" rx="0.5"/>
                <rect x="1" y="12" width="14" height="3" rx="0.5"/>
              </svg>
            </button>
            {/* D1: Find in Archive (opens WorkspaceCurator modal) */}
            <button
              className="workspace__icon-btn"
              onClick={() => { setCuratorInitialQuery(null); setCuratorInitialFindBuffer(undefined); setShowCurator(true); }}
              title="Find in Archive"
              aria-label="Find in Archive"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242.656a5 5 0 1 1 0-10 5 5 0 0 1 0 10z"/>
              </svg>
            </button>
            {activeProject && activeChainBufferId && (
              <button
                className="workspace__icon-btn"
                onClick={async () => {
                  try {
                    await addBufferToProject(activeChainBufferId!);
                  } catch (err) {
                    log.error('Failed to save to project:', err);
                  }
                }}
                title={`Save to "${activeProject.name}"`}
                aria-label="Save to project"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H2Zm6 3.5a.75.75 0 0 1 .75.75v2h2a.75.75 0 0 1 0 1.5h-2v2a.75.75 0 0 1-1.5 0v-2h-2a.75.75 0 0 1 0-1.5h2v-2A.75.75 0 0 1 8 4.5Z"/>
                </svg>
              </button>
            )}
            {content && (
              <button
                className="workspace__icon-btn"
                onClick={async () => {
                  await navigator.clipboard.writeText(thread.displayText);
                }}
                title="Copy as plain text"
                aria-label="Copy"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 0 1 0 1.5h-1.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 9.25 16h-7.5A1.75 1.75 0 0 1 0 14.25v-7.5Z"/>
                  <path d="M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0 1 14.25 11h-7.5A1.75 1.75 0 0 1 5 9.25v-7.5Zm1.75-.25a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-7.5a.25.25 0 0 0-.25-.25h-7.5Z"/>
                </svg>
              </button>
            )}
            {content && (
              <button
                className="workspace__icon-btn"
                onClick={() => {
                  const blob = new Blob([thread.displayText], { type: 'text/markdown' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${content.title || 'content'}.md`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                title="Download as markdown"
                aria-label="Download"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M2.75 14A1.75 1.75 0 0 1 1 12.25v-2.5a.75.75 0 0 1 1.5 0v2.5c0 .138.112.25.25.25h10.5a.25.25 0 0 0 .25-.25v-2.5a.75.75 0 0 1 1.5 0v2.5A1.75 1.75 0 0 1 13.25 14H2.75Z"/>
                  <path d="M7.25 7.689V2a.75.75 0 0 1 1.5 0v5.689l1.97-1.969a.749.749 0 1 1 1.06 1.06l-3.25 3.25a.749.749 0 0 1-1.06 0L4.22 6.78a.749.749 0 1 1 1.06-1.06l1.97 1.969Z"/>
                </svg>
              </button>
            )}
            {content && (onFindSimilar || true) && (
              <OverflowMenu
                onFindSimilar={onFindSimilar ? () => onFindSimilar(thread.displayText) : undefined}
                onFindRelated={selectedText ? () => {
                  setCuratorInitialQuery(selectedText);
                  setCuratorInitialFindBuffer(undefined);
                  setShowCurator(true);
                } : undefined}
                onAddToVoice={async () => {
                  if (isLocalArchive) {
                    log.warn('Voice synthesis not available in local mode');
                    return;
                  }
                  try {
                    const voice = await api.userBuffers.getVoice();
                    const existing = (voice.workingContent ?? []) as ContentItem[];
                    const existingSamples = existing.map((item) => item.text).filter(Boolean);
                    const allSamples = [...existingSamples, thread.displayText];
                    const { persona } = await api.transform.synthesizeVoice(allSamples);
                    const voiceItem: ContentItem = {
                      id: crypto.randomUUID(),
                      type: 'text',
                      text: thread.displayText,
                      metadata: {
                        source: { type: 'voice-synthesized', path: ['Workspace'] },
                        timestamp: Date.now(),
                        wordCount: thread.displayText.split(/\s+/).filter(Boolean).length,
                        synthesizedPersona: persona,
                      },
                    };
                    const updatedItems = existing.map((item) => {
                      const { synthesizedPersona: _, ...meta } = item.metadata as Record<string, unknown>;
                      return { ...item, metadata: meta };
                    });
                    await api.userBuffers.updateContent(voice.name, [voiceItem, ...updatedItems]);
                  } catch (err) {
                    log.error('Failed to synthesize voice:', err);
                  }
                }}
                onExportMarkdown={thread.isThreadMode && messages?.[0]?.metadata?.source?.threadId && window.electronArchive?.exportThreadMarkdown
                  ? async () => {
                    const threadId = messages![0].metadata.source?.threadId;
                    if (!threadId) return;
                    try {
                      await window.electronArchive!.exportThreadMarkdown(threadId);
                    } catch (err) {
                      log.error('Export failed:', err);
                    }
                  }
                  : undefined
                }
                onExportPdf={thread.isThreadMode && messages?.[0]?.metadata?.source?.threadId && window.electronArchive?.exportThreadPdf
                  ? async () => {
                    const threadId = messages![0].metadata.source?.threadId;
                    if (!threadId) return;
                    try {
                      await window.electronArchive!.exportThreadPdf(threadId);
                    } catch (err) {
                      log.error('Export failed:', err);
                    }
                  }
                  : undefined
                }
              />
            )}
          </div>
        </div>

        {/* Sync error banner */}
        {syncError && (
          <div className="workspace__sync-error" role="alert">
            {syncError}
          </div>
        )}

        {/* Workspace Mode Tabs */}
        <div className="workspace__mode-tabs" role="tablist" aria-label="Workspace mode">
          <button
            className={`workspace__mode-tab${workspaceMode === 'archive' ? ' workspace__mode-tab--active' : ''}`}
            role="tab"
            aria-selected={workspaceMode === 'archive'}
            onClick={() => setWorkspaceMode('archive')}
          >
            Archive
          </button>
          <button
            className={`workspace__mode-tab${workspaceMode === 'project' ? ' workspace__mode-tab--active' : ''}`}
            role="tab"
            aria-selected={workspaceMode === 'project'}
            onClick={() => setWorkspaceMode('project')}
          >
            Project
          </button>
        </div>

        {/* Row 2: View + Content controls */}
        <div className="workspace__toolbar-view">
          <div className="workspace__view-toggle" role="group" aria-label="View mode">
            {/* Archive mode buttons: Read, Edit, Split */}
            {workspaceMode === 'archive' && (
              <>
                <button
                  className={`workspace__view-btn ${viewMode === 'read' ? 'workspace__view-btn--active' : ''}`}
                  onClick={() => setViewMode('read')}
                  aria-pressed={viewMode === 'read'}
                  title="Read mode (Cmd+E to cycle)"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M8 3.5a4.5 4.5 0 0 0-4.34 3.31.75.75 0 0 0 0 .38A4.5 4.5 0 0 0 8 10.5a4.5 4.5 0 0 0 4.34-3.31.75.75 0 0 0 0-.38A4.5 4.5 0 0 0 8 3.5ZM1.5 8a6.5 6.5 0 0 1 13 0 6.5 6.5 0 0 1-13 0ZM8 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z"/>
                  </svg>
                  Read
                </button>
                <button
                  className={`workspace__view-btn ${viewMode === 'edit' ? 'workspace__view-btn--active' : ''}`}
                  onClick={() => edit.handleEnterEdit()}
                  aria-pressed={viewMode === 'edit'}
                  disabled={!content}
                  title={!content ? 'Load content to edit' : 'Edit mode (Cmd+E to cycle)'}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25a1.75 1.75 0 0 1 .445-.758l8.61-8.61Z"/>
                  </svg>
                  Edit
                </button>
                <button
                  className={`workspace__view-btn ${viewMode === 'split' ? 'workspace__view-btn--active' : ''}`}
                  onClick={() => hasSplit && setViewMode('split')}
                  aria-pressed={viewMode === 'split'}
                  disabled={!hasSplit}
                  title={hasSplit ? 'Split view (Cmd+E to cycle)' : 'Run a transformation to enable split view'}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M1.75 2A1.75 1.75 0 0 0 0 3.75v8.5C0 13.216.784 14 1.75 14h12.5A1.75 1.75 0 0 0 16 12.25v-8.5A1.75 1.75 0 0 0 14.25 2H1.75ZM7.25 3.5v9H1.75a.25.25 0 0 1-.25-.25v-8.5a.25.25 0 0 1 .25-.25h5.5Zm1.5 9v-9h5.5a.25.25 0 0 1 .25.25v8.5a.25.25 0 0 1-.25.25h-5.5Z"/>
                  </svg>
                  Split
                </button>
              </>
            )}
            {/* Project mode buttons: Stack, Plan, Outline, Draft */}
            {workspaceMode === 'project' && (
              <>
                <button
                  className={`workspace__view-btn ${viewMode === 'stack' ? 'workspace__view-btn--active' : ''}`}
                  onClick={() => setViewMode('stack')}
                  aria-pressed={viewMode === 'stack'}
                  title={`Stack view (${stackItems.length} cards) — Cmd+E to cycle`}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M8 1.5l6.5 3L8 7.5 1.5 4.5 8 1.5zm-6.5 6L8 10.5l6.5-3L8 4.5 1.5 7.5zm0 3L8 13.5l6.5-3L8 7.5 1.5 10.5z"/>
                  </svg>
                  Stack{stackItems.length > 0 ? ` (${stackItems.length})` : ''}
                </button>
                <button
                  className={`workspace__view-btn ${viewMode === 'book-plan' ? 'workspace__view-btn--active' : ''}`}
                  onClick={() => setViewMode('book-plan')}
                  aria-pressed={viewMode === 'book-plan'}
                  disabled={stackItems.length < 3}
                  title={stackItems.length < 3 ? 'Add at least 3 cards to plan a book' : 'Plan book structure'}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M1 2.828c.885-.37 2.154-.769 3.388-.893 1.33-.134 2.458.063 3.112.752v9.746c-.935-.53-2.12-.603-3.213-.493-1.18.12-2.37.461-3.287.811V2.828zm7.5-.141c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492V2.687zM8 1.783C7.015.936 5.587.81 4.287.94c-1.514.153-3.042.672-3.994 1.105A.5.5 0 0 0 0 2.5v11a.5.5 0 0 0 .707.455c.882-.4 2.303-.881 3.68-1.02 1.409-.142 2.59.087 3.223.877a.5.5 0 0 0 .78 0c.633-.79 1.814-1.019 3.222-.877 1.378.139 2.8.62 3.681 1.02A.5.5 0 0 0 16 13.5v-11a.5.5 0 0 0-.293-.455c-.952-.433-2.48-.952-3.994-1.105C10.413.81 8.985.936 8 1.783z"/>
                  </svg>
                  Plan
                </button>
                <button
                  className={`workspace__view-btn ${viewMode === 'outline' ? 'workspace__view-btn--active' : ''}`}
                  onClick={handleOutlineClick}
                  aria-pressed={viewMode === 'outline'}
                  disabled={stackItems.length < 2}
                  title={stackItems.length < 2 ? 'Add at least 2 cards to generate an outline' : 'Outline view — Cmd+E to cycle'}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M2 3.5h12v1H2zM4 6.5h10v1H4zM4 9.5h10v1H4zM2 12.5h12v1H2z"/>
                  </svg>
                  Outline
                </button>
                <button
                  className={`workspace__view-btn ${viewMode === 'draft' ? 'workspace__view-btn--active' : ''}`}
                  onClick={() => setViewMode('draft')}
                  aria-pressed={viewMode === 'draft'}
                  disabled={!draft.draftText}
                  title={!draft.draftText ? 'Generate an outline first, then create a draft' : 'Draft view — Cmd+E to cycle'}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h8.586a1.5 1.5 0 0 1 1.06.44l2.415 2.414A1.5 1.5 0 0 1 15 4.914V13.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 1 13.5v-11Zm1.5 0v11h11V4.914L11.086 2.5H2.5ZM4 7h8v1H4V7Zm0 2.5h8v1H4v-1Zm0 2.5h5v1H4V12Z"/>
                  </svg>
                  Draft
                </button>
              </>
            )}
          </div>

          {/* Thread navigation controls */}
          {thread.isThreadMode && viewMode === 'read' && (
            <MessageNavigator
              currentIndex={thread.activeMessageIndex}
              totalCount={messages!.length}
              onFirst={thread.goFirst}
              onPrev={thread.goPrev}
              onNext={thread.goNext}
              onLast={thread.goLast}
            />
          )}

          {/* Role filters (thread mode only) */}
          {thread.isThreadMode && viewMode === 'read' && (
            <RoleFilter filters={thread.filters} onFiltersChange={thread.setFilters} />
          )}

          <span className="workspace__view-hint" aria-hidden="true">
            Cmd+E
          </span>
          {isDirty && (
            <span className="workspace__dirty-indicator" title="Unsaved changes">
              •
            </span>
          )}
          {draft.saveStatus === 'saving' && (
            <span className="workspace__save-status workspace__save-status--saving">Saving…</span>
          )}
          {draft.saveStatus === 'saved' && (
            <span className="workspace__save-status workspace__save-status--saved">Saved</span>
          )}
        </div>
      </div>

      {/* Buffer History */}
      <BufferHistory />

      {/* Transform Chain (VersionBar) — hidden in stack mode */}
      {transformChain.length > 1 && viewMode !== 'stack' && (
        <VersionBar
          versions={transformChain.map(entry => ({
            id: entry.id,
            type: (entry.type.startsWith('rewrite_') || entry.type.startsWith('transform_') ? 'transform' : 'archive') as ContentVersion['type'],
            label: entry.label.charAt(0).toUpperCase() + entry.label.slice(1),
            timestamp: entry.timestamp,
          }))}
          activeVersionId={activeChainBufferId}
          onVersionSelect={switchToChainVersion}
        />
      )}

      {/* Content Area */}
      {viewMode === 'book-plan' ? (
        <BookPlanView onPlanApplied={() => setViewMode('outline')} />
      ) : viewMode === 'outline' ? (
        stackItems.length >= 2 ? (
          <OutlinePanelInline
            stackItems={stackItems}
            initialState={draft.outlineState}
            onStateChange={draft.setOutlineState}
            onDraftCreated={draft.handleDraftCreatedAndSwitch}
            bookContext={bookContext}
            noemaGraphData={noemaGraphData}
            sicData={sicData}
            chapterGraphData={chapterGraphData}
          />
        ) : (
          <div className="workspace__empty-mode">
            <p>Add at least 2 cards to generate an outline.</p>
            <button className="workspace__empty-mode-btn" onClick={() => setViewMode('stack')}>Go to Stack</button>
          </div>
        )
      ) : viewMode === 'draft' ? (
        draft.draftText ? (
          <DraftEditorInline
            draftText={draft.draftText}
            onDraftTextChange={draft.setDraftText}
            onFlush={draft.handleDraftFlush}
            sourceCards={draft.draftSourceCards}
            provenances={draft.draftProvenances}
            generationModel={draft.draftGenerationModel}
            humanizationModel={draft.draftHumanizationModel}
            humanized={draft.draftHumanized}
            generationTimeMs={draft.draftGenerationTimeMs}
            humanizationTimeMs={draft.draftHumanizationTimeMs}
            targetWordCount={draft.draftTargetWordCount}
            draftBufferId={draft.draftBufferId}
            versionRefreshTrigger={draft.versionRefreshTrigger}
            qualityReport={draft.draftQualityReport}
            intentionalDrift={draft.draftIntentionalDrift}
            onViewWithSources={(text, cards, provs) => {
              setSourceReferenceView({ draftText: text, sourceCards: cards, provenances: provs });
              setViewMode('split');
            }}
          />
        ) : (
          <div className="workspace__empty-mode">
            <p>Generate a draft from the Outline view first.</p>
            <button className="workspace__empty-mode-btn" onClick={() => setViewMode('outline')} disabled={stackItems.length < 2}>Go to Outline</button>
          </div>
        )
      ) : viewMode === 'stack' ? (
        <CardStack isEditing={stackEditMode} onEditingChange={setStackEditMode} onViewWithSources={handleViewWithSources} onDraftCreated={draft.handleDraftCreatedAndSwitch} onOutlineOpen={handleCardStackOutlineOpen} />
      ) : !content ? (
        <WelcomeScreen />
      ) : viewMode === 'split' && sourceReferenceView ? (
        <WorkspaceSplitView
          mode="sources"
          sourceReferenceView={sourceReferenceView}
          splitContent={null}
          activeSourceIndex={activeSourceIndex}
          onActiveSourceIndexChange={(idx) => setActiveSourceIndex(idx)}
          onDismissSourceView={handleDismissSourceView}
        />
      ) : viewMode === 'split' && splitContent ? (
        <WorkspaceSplitView
          mode="transform"
          sourceReferenceView={null}
          splitContent={splitContent}
          activeSourceIndex={null}
          onActiveSourceIndexChange={() => {}}
          onDismissSourceView={() => {}}
        />
      ) : viewMode === 'read' ? (
        <WorkspaceReadView
          isThreadMode={thread.isThreadMode}
          messages={messages}
          currentMessage={thread.currentMessage}
          activeMessageIndex={thread.activeMessageIndex}
          filteredIndices={thread.filteredIndices}
          onJumpToMessage={thread.jumpToMessage}
          displayText={thread.displayText}
          displayRole={thread.displayRole}
          content={content}
          highlightText={highlightText}
          sentencingData={sentencingData}
          roleClassifierData={roleClassifierData}
          onConversationClick={onConversationClick}
          onMediaClick={onMediaClick}
          onAddMessageToStack={handleAddMessageToStack}
        />
      ) : (
        <WorkspaceEditView
          editContent={editContent}
          onEditContentChange={setEditContent}
          onApplyChanges={edit.handleApplyChanges}
          onSaveOverride={override.handleSaveOverride}
          onCancel={edit.handleCancelEdit}
          onUndo={undo}
          onRedo={redo}
          isSaving={edit.isSaving}
          isSavingOverride={override.isSavingOverride}
          canUndo={canUndo}
          canRedo={canRedo}
          hasNodeId={!!override.currentNodeId}
        />
      )}

      {/* D1: Workspace Curator modal */}
      {showCurator && (
        <WorkspaceCurator
          onClose={() => { setShowCurator(false); setCuratorInitialFindBuffer(undefined); }}
          initialQuery={curatorInitialQuery}
          initialFindBuffer={curatorInitialFindBuffer}
        />
      )}

      {/* Buffer Manager drawer */}
      <BufferManagerDrawer
        isOpen={showBufferManager}
        onClose={() => setShowBufferManager(false)}
      />
    </div>
  );
}
